import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check } from "lucide-react";

const OFFICIAL_ROLES = ["chief", "deputy_chief", "head_councillor", "councillor", "executive_director", "band_registry_clerk"];

export default function Polls() {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [tab, setTab] = useState("official");
  const [showCreate, setShowCreate] = useState(false);
  const canOfficial = OFFICIAL_ROLES.includes(user?.role) || user?.is_super_admin;

  const load = () => api.get("/polls").then(r=>setPolls(r.data));
  useEffect(() => { load(); }, []);

  const filtered = polls.filter(p => p.poll_type === tab);
  const vote = async (id, idx) => {
    try {
      await api.post(`/polls/${id}/vote`, { option_index: idx });
      toast.success("Vote recorded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Vote failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#89b4fa]">CONSULTATION MODULE</div>
          <h1 className="font-display text-4xl mt-2">Polls · Surveys · Ballots</h1>
        </div>
        <button className="btn-cta primary" onClick={()=>setShowCreate(true)} data-testid="create-poll-btn">
          <Plus size={12} /> New Item
        </button>
      </div>

      <div className="flex gap-1 mt-8 border-b border-white/10">
        {[["official","Official (Chief & Council)"], ["community","Community"]].map(([k,v]) => (
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2 ${tab===k?"border-[#89b4fa] text-[#89b4fa]":"border-transparent text-white/50"}`} data-testid={`tab-${k}`}>
            {v}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {filtered.length === 0 && <div className="font-mono text-xs text-white/40">No {tab} items yet.</div>}
        {filtered.map(p => (
          <div key={p.id} className="panel p-6" data-testid={`poll-${p.id}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase" style={{color: p.poll_type==='official'?'#ef5b6b':'#d4b876'}}>
                  {p.category} · {ROLE_LABEL[p.created_by_role]} · {p.created_by_name}
                </div>
                <div className="font-display text-2xl mt-1">{p.title}</div>
                {p.description && <div className="text-white/60 mt-2 max-w-2xl">{p.description}</div>}
              </div>
              <div className="font-mono text-[10px] text-white/40">{p.total_votes} votes</div>
            </div>
            <div className="mt-5 space-y-2">
              {p.options.map(opt => {
                const pct = p.total_votes ? Math.round((opt.votes / p.total_votes) * 100) : 0;
                return (
                  <button key={opt.index} disabled={p.has_voted} onClick={()=>vote(p.id, opt.index)}
                    className={`w-full text-left border p-3 relative overflow-hidden ${p.has_voted ? "border-white/10 cursor-default" : "border-white/20 hover:border-[#89b4fa]"}`}
                    data-testid={`poll-option-${p.id}-${opt.index}`}>
                    <div className="absolute inset-0 bg-[#89b4fa]/10" style={{width: `${pct}%`}} />
                    <div className="relative flex items-center justify-between">
                      <span className="font-mono text-sm">{opt.label}</span>
                      <span className="font-mono text-xs text-white/60">{opt.votes} · {pct}%</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {p.has_voted && <div className="mt-3 font-mono text-[10px] text-[#89b4fa] uppercase tracking-widest flex items-center gap-2"><Check size={12}/> Vote recorded</div>}
          </div>
        ))}
      </div>

      {showCreate && <CreateModal onClose={()=>setShowCreate(false)} onCreated={load} canOfficial={canOfficial} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated, canOfficial }) {
  const [f, setF] = useState({ title: "", description: "", poll_type: "community", category: "poll", options: ["", ""] });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const opts = f.options.map(o=>o.trim()).filter(Boolean);
      await api.post("/polls", { ...f, options: opts });
      toast.success(`${f.category.charAt(0).toUpperCase()+f.category.slice(1)} published`);
      onCreated(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg border border-white/20 bg-[#181825] p-6 space-y-4">
        <div className="label-mono text-[#89b4fa]">NEW CONSULTATION ITEM</div>
        <input placeholder="Title" required value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="new-poll-title" />
        <textarea placeholder="Description" rows={3} value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="new-poll-desc" />
        <div className="grid grid-cols-2 gap-3">
          <select value={f.poll_type} onChange={e=>setF({...f, poll_type:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid="new-poll-type">
            <option value="community">Community</option>
            {canOfficial && <option value="official">Official</option>}
          </select>
          <select value={f.category} onChange={e=>setF({...f, category:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid="new-poll-category">
            <option value="poll">Poll</option>
            <option value="ballot">Ballot</option>
          </select>
        </div>
        {f.options.map((o, i) => (
          <input key={i} placeholder={`Option ${i+1}`} value={o} onChange={e=>{ const n=[...f.options]; n[i]=e.target.value; setF({...f, options:n}); }} className="w-full px-3 py-2 font-mono text-sm" data-testid={`new-poll-option-${i}`} />
        ))}
        <button type="button" onClick={()=>setF({...f, options:[...f.options, ""]})} className="btn-cta text-xs" data-testid="add-poll-option">+ Add option</button>
        {err && <div className="text-[#ef5b6b] font-mono text-xs">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="submit-poll-btn">{saving?"Posting...":"Publish"}</button>
        </div>
      </form>
    </div>
  );
}
